import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Modal,
  Form,
  DatePicker,
  Divider,
  InputNumber,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Drawer,
  Descriptions,
  Tabs,
  Empty,
  Spin,
  Badge,
  List,
  FloatButton,
  Grid,
  Alert,
  Switch,
  Radio
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  PrinterOutlined,
  PayCircleOutlined,
  MoneyCollectOutlined,
  FilterOutlined,
  ScanOutlined,
  BarcodeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getReceipts,
  getReceiptById,
  createReceipt,
  updateReceipt,
  deleteReceipt,
  payReceipt,
  getSalesStats,
  getReceiptStatusLabel,
  getReceiptStatusColor
} from '../../services/receiptService';
import { getCustomersForDropdown } from '../../services/contactService';
import { getProducts, getProductByBarcode } from '../../services/productService';
import { generateReceiptPDF } from '../../services/receiptPdfService';
import {
  getCompanySettings,
  updateCompanySettings,
  uploadCompanyLogo,
  deleteCompanyLogo,
  getLogoUrl
} from '../../services/settingsService';
import { downloadReceiptsTablePDF } from '../../services/receiptTablePdfService';
import { useAuth } from '../../contexts/AuthContext'
import { User2Icon } from 'lucide-react';

const { Title, Text } = Typography
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const ReceiptsPage = () => {

  const { user } = useAuth()
  const [printType, setPrintType] = useState("full");
  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  // Calculate sales stats from receipts array directly
  const salesStats = useMemo(() => {
    const total_sales = receipts.reduce((sum, receipt) => sum + parseFloat(receipt.total_amount || 0), 0);
    const total_receipts = receipts.length;
    const paid_receipts = receipts.filter(r => r.status === 'paid').length;
    const outstanding_amount = receipts
      .filter(r => r.status !== 'paid' && r.status !== 'cancelled')
      .reduce((sum, receipt) => sum + (parseFloat(receipt.total_amount || 0) - parseFloat(receipt.paid_amount || 0)), 0);

    return {
      total_sales,
      total_receipts,
      paid_receipts,
      outstanding_amount
    };
  }, [receipts]);
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();

  // Responsive state
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);



  //สำหรับ modal แสดง pdf
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfPreviewReceipt, setPdfPreviewReceipt] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ข้อมูลบริษัท
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  // Pagination and filters
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    contact_id: '',
  });
  const [statusCounts, setStatusCounts] = useState([]);

  // Barcode states
  const [barcodeMode, setBarcodeMode] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const barcodeInputRef = useRef(null);

  // Date Filter State
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('all');
  const { RangePicker } = DatePicker;

  // Date filter handlers
  const handleQuickDateFilter = (filterType) => {
    setSelectedQuickFilter(filterType);
    const today = dayjs();
    let startDate, endDate;

    switch (filterType) {
      case 'this_month':
        startDate = today.clone().startOf('month');
        endDate = today.clone().endOf('month');
        break;
      case 'last_month':
        startDate = today.clone().subtract(1, 'month').startOf('month');
        endDate = today.clone().subtract(1, 'month').endOf('month');
        break;
      case 'last_3_months':
        startDate = today.clone().subtract(3, 'months').startOf('month');
        endDate = today.clone().endOf('month');
        break;
      case 'last_6_months':
        startDate = today.clone().subtract(6, 'months').startOf('month');
        endDate = today.clone().endOf('month');
        break;
      case 'this_year':
        startDate = today.clone().startOf('year');
        endDate = today.clone().endOf('year');
        break;
      case 'last_year':
        startDate = today.clone().subtract(1, 'year').startOf('year');
        endDate = today.clone().subtract(1, 'year').endOf('year');
        break;
      default:
        startDate = null;
        endDate = null;
    }

    setSelectedDateRange([startDate, endDate]);

    // Update filters
    setFilters(prev => ({
      ...prev,
      start_date: startDate ? startDate.format('YYYY-MM-DD') : '',
      end_date: endDate ? endDate.format('YYYY-MM-DD') : ''
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    const dateRange = dates || [null, null];
    setSelectedDateRange(dateRange);
    setSelectedQuickFilter('custom');

    setFilters(prev => ({
      ...prev,
      start_date: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : '',
      end_date: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : ''
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearDateFilter = () => {
    const dateRange = [null, null];
    setSelectedDateRange(dateRange);
    setSelectedQuickFilter('all');

    setFilters(prev => ({
      ...prev,
      start_date: '',
      end_date: ''
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Status options
  const statusOptions = [
    { value: 'invoice', label: 'ใบแจ้งหนี้', color: 'processing' },
    { value: 'paid', label: 'ชำระแล้ว', color: 'success' },
    { value: 'partial', label: 'ชำระบางส่วน', color: 'warning' },
    { value: 'overdue', label: 'เกินกำหนด', color: 'error' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'default' },
  ];

  // Payment methods
  const paymentMethods = [
    'เงินสด',
    'โอนผ่านธนาคาร',
    'เช็ค',
    'บัตรเครดิต',
    'บัตรเดบิต',
    'อื่นๆ'
  ];

  // VAT modes
  const vatModeOptions = [
    { value: 'no_tax', label: 'ไม่มีภาษี', color: 'green' },
    { value: 'exclude', label: 'แยกภาษี (VAT EXCLUDE)', color: 'blue' },
    { value: 'include', label: 'รวมภาษี (VAT INCLUDE)', color: 'green' }
  ];

  // โหลดค่าจาก localStorage ครั้งแรกตอนเปิด Modal
  useEffect(() => {
    if (modalVisible) {
      const savedType = localStorage.getItem('PRINT_TYPE');
      if (savedType) {
        setPrintType(savedType);
      }
    }
  }, [modalVisible]);

  const handleDownloadPdf = () => {
    if (!pdfDataUrl) return;

    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = pdfFileName || 'receipt.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!pdfModalVisible || !pdfPreviewReceipt) {
      return;
    }

    let isActive = true;

    const refreshPdfPreview = async () => {
      setPdfLoading(true);
      setPdfDataUrl('');

      try {
        const pdfResult = await generateReceiptPDF(pdfPreviewReceipt, company || {}, printType);

        if (isActive) {
          setPdfDataUrl(pdfResult.dataUrl);
          setPdfFileName(pdfResult.fileName);
        }
      } catch (error) {
        console.error('PDF preview refresh error:', error);

        if (isActive) {
          message.error('เกิดข้อผิดพลาดในการอัปเดตตัวอย่าง PDF: ' + error.message);
        }
      } finally {
        if (isActive) {
          setPdfLoading(false);
        }
      }
    };

    refreshPdfPreview();

    return () => {
      isActive = false;
    };
  }, [pdfModalVisible, pdfPreviewReceipt, company, printType]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getCompanySettings();
      if (response.data.success) {
        const data = response.data.data;
        setCompany(data);
        form.setFieldsValue(data);
        setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
      }
    } catch (error) {
      //console.error('Error fetching settings:', error);
      //message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
    } finally {
      setLoading(false);
    }
  };

  // Load data
  useEffect(() => {
    loadReceipts();
    loadCustomers();
    loadProducts();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await getReceipts({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      });

      if (response.data.success) {
        setReceipts(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
        setStatusCounts(response.data.statusCounts || []);
      }
    } catch (error) {
      //console.error('Error loading receipts:', error);
      //message.error('ไม่สามารถโหลดข้อมูลใบเสร็จได้');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await getCustomersForDropdown();
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      //console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts({ limit: 1000 });
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };



  const calculateTotals = (items, discount = 0, taxRate = 7, vatMode = 'exclude', whtRate = 0) => {
    const totalBeforeDiscount = (items || []).reduce((sum, item) => {
      if (!item) return sum;               // เช็ค undefined
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);

    const discountAmount = parseFloat(discount) || 0;

    let subtotal = 0;
    let taxAmount = 0;
    let totalAmount = 0;
    let whtAmount = 0;

    if (vatMode === 'include') {
      totalAmount = totalBeforeDiscount - discountAmount;
      subtotal = totalAmount / (1 + taxRate / 100);
      taxAmount = totalAmount - subtotal;
    } else if (vatMode === 'exclude') {
      subtotal = totalBeforeDiscount - discountAmount;
      taxAmount = (subtotal * taxRate) / 100;
      totalAmount = subtotal + taxAmount;
    } else {
      subtotal = totalBeforeDiscount - discountAmount;
      taxAmount = 0;
      totalAmount = subtotal;
    }

    if (whtRate > 0) {
      whtAmount = (subtotal * whtRate) / 100;
    }

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      discount: parseFloat(discountAmount.toFixed(2)),
      subtotalAfterDiscount: parseFloat((subtotal).toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      whtAmount: parseFloat(whtAmount.toFixed(2))
    };
  };


  // Barcode functions
  const handleBarcodeScanned = async (barcode) => {
    if (!barcode || barcode.trim() === '') return;

    try {
      setBarcodeLoading(true);

      const product = await findProductByBarcode(barcode.trim());

      if (product) {
        addProductToItems(product);
        message.success(`เพิ่มสินค้า: ${product.name}`);

        setBarcodeInput('');
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      } else {
        message.warning(`ไม่พบสินค้าที่มีบาร์โค้ด: ${barcode}`);
        setBarcodeInput('');
      }
    } catch (error) {
      console.error('Error scanning barcode:', error);
      message.error('เกิดข้อผิดพลาดในการสแกนบาร์โค้ด');
      setBarcodeInput('');
    } finally {
      setBarcodeLoading(false);
    }
  };

  const findProductByBarcode = async (barcode) => {
    return products.find(product =>
      product.barcode && product.barcode === barcode
    );
  };

  const addProductToItems = (product) => {
    const currentItems = form.getFieldValue('items') || [];

    const existingItemIndex = currentItems.findIndex(item =>
      item.product_id === product.id
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...currentItems];
      updatedItems[existingItemIndex].quantity = (updatedItems[existingItemIndex].quantity || 1) + 1;
      form.setFieldsValue({ items: updatedItems });
    } else {
      const newItem = {
        product_id: product.id,
        description: product.name,
        quantity: 1,
        unit_price: product.price || 0
      };

      form.setFieldsValue({
        items: [...currentItems, newItem]
      });
    }
  };

  const handleBarcodeInputChange = (e) => {
    setBarcodeInput(e.target.value);
  };

  const handleBarcodeInputKeyPress = (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      handleBarcodeScanned(barcodeInput);
    }
  };

  const clearBarcodeInput = () => {
    setBarcodeInput('');
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  // Modal handlers
  const showCreateModal = () => {
    setEditingReceipt(null);
    setModalVisible(true);
    setBarcodeMode(false);
    setBarcodeInput('');
    form.resetFields();
    form.setFieldsValue({
      receipt_date: dayjs(),
      tax_rate: 7,
      vat_mode: 'exclude',
      items: [{ description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const [calcValues, setCalcValues] = useState({
    subtotal: 0,
    discount: 0,
    subtotalAfterDiscount: 0,
    taxAmount: 0,
    totalAmount: 0
  });

  const calculateSummary = (items = [], discount = 0, taxRate = 0, vatMode = 'exclude') => {
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);

    const discountAmount = parseFloat(discount) || 0;
    const subtotalAfterDiscount = subtotal - discountAmount;

    let taxAmount = 0;
    if (vatMode === 'include') {
      taxAmount = subtotalAfterDiscount - subtotalAfterDiscount / (1 + taxRate / 100);
    } else {
      taxAmount = subtotalAfterDiscount * (taxRate / 100);
    }

    const totalAmount = subtotalAfterDiscount + (vatMode === 'exclude' ? taxAmount : 0);

    return {
      subtotal,
      discount: discountAmount,
      subtotalAfterDiscount,
      taxAmount,
      totalAmount
    };
  };

  const showEditModal = (record) => {
    setEditingReceipt(record);
    setModalVisible(true);
    setBarcodeMode(false);
    setBarcodeInput('');

    form.setFieldsValue({
      contact_id: record.contact_id,
      receipt_date: dayjs(record.receipt_date),
      due_date: record.due_date ? dayjs(record.due_date) : null,
      discount: record.discount,
      tax_rate: record.tax_rate,
      vat_mode: record.vat_mode || 'include',
      total_amount: record.total_amount,
      tax_amount: record.tax_amount,
      subtotal: record.subtotal,
      notes: record.notes,
      status: record.status
    });

    if (record.id) {
      loadReceiptForEdit(record.id);
    }
  };

  // --------- แก้ไข loadReceiptForEdit ---------
  const loadReceiptForEdit = async (id) => {
    try {
      const response = await getReceiptById(id);
      if (response.data.success) {
        const receipt = response.data.data;

        const items = (receipt.items && receipt.items.length > 0)
          ? receipt.items.map(item => ({
            product_id: item.product_id,
            description: item.description,
            quantity: parseFloat(item.quantity) || 0,
            unit_price: parseFloat(item.unit_price) || 0
          }))
          : [{ description: '-', quantity: 1, unit_price: 0 }];

        // ใส่ค่าในฟอร์ม
        form.setFieldsValue({
          ...form.getFieldsValue(),
          contact_id: receipt.contact_id,
          receipt_date: receipt.receipt_date ? dayjs(receipt.receipt_date) : dayjs(),
          due_date: receipt.due_date ? dayjs(receipt.due_date) : undefined,
          invoice_number: receipt.invoice_number,
          receipt_number: receipt.receipt_number,
          discount: parseFloat(receipt.discount) || 0,
          tax_rate: parseFloat(receipt.tax_rate) || 7,
          vat_mode: receipt.vat_mode || 'exclude',
          status: receipt.status || 'invoice',
          totalAmount: receipt.totalAmount,
          notes: receipt.notes || '',
          items
        });

        setCalcValues({
          subtotal: 1,
          discount: 1,
          subtotalAfterDiscount: 1,
          taxAmount: 1,
          totalAmount: 1
        })

        // ✅ คำนวณสรุปยอดทันทีหลังโหลด
        handleFormChange();
      }
    } catch (error) {
      console.error('Error loading receipt for edit:', error);
    }
  };


  const handleFormChange = () => {
    const items = form.getFieldValue('items') || [];
    const discount = form.getFieldValue('discount') || 0;
    const taxRate = form.getFieldValue('tax_rate') || 7;
    const currentVatMode = form.getFieldValue('vat_mode') || 'exclude';

    const calculations = calculateTotals(items, discount, taxRate, currentVatMode);
    setCalcValues(calculations);
  };

  const showDetailDrawer = async (record) => {
    try {
      setLoading(true);
      const response = await getReceiptById(record.id);
      if (response.data.success) {
        setSelectedReceipt(response.data.data);
        setDetailDrawerVisible(true);
      }
    } catch (error) {
      console.error('Error loading receipt details:', error);
      message.error('ไม่สามารถโหลดรายละเอียดใบเสร็จได้');
    } finally {
      setLoading(false);
    }
  };

  const showPaymentModal = (record) => {
    setPaymentReceipt(record);
    setPaymentModalVisible(true);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({
      paid_amount: record.total_amount - (record.paid_amount || 0)
    });
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingReceipt(null);
    setBarcodeMode(false);
    setBarcodeInput('');
    form.resetFields();
  };

  const handleClosePaymentModal = () => {
    setPaymentModalVisible(false);
    setPaymentReceipt(null);
    paymentForm.resetFields();
  };

  const handleClosePdfModal = () => {
    setPdfModalVisible(false);
    setPdfDataUrl('');
    setPdfFileName('');
    setPdfPreviewReceipt(null);
    setPdfLoading(false);
  };

  // CRUD operations
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const items = (values.items || []).map(item => ({
        product_id: item.product_id || null,
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(String(item.unit_price).replace(/,/g, '')) || 0
      }));

      const discount = values.discount || 0;
      const taxRate = values.tax_rate || 0;
      const vatMode = values.vat_mode || 'exclude';

      const calcValues = calculateTotals(items, discount, taxRate, vatMode);



      const receiptData = {
        ...values,
        receipt_date: values.receipt_date.format('YYYY-MM-DD'),
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
        subtotal: calcValues.subtotal,
        discount: calcValues.discount,
        tax_amount: calcValues.taxAmount,
        total_amount: calcValues.totalAmount,
        vat_mode: vatMode,
        items // ✅ ใช้ที่ parse แล้ว
      };

      if (editingReceipt) {
        await updateReceipt(editingReceipt.id, receiptData);
        message.success('แก้ไขใบเสร็จเรียบร้อยแล้ว');
      } else {
        await createReceipt(receiptData);
        message.success('สร้างใบเสร็จเรียบร้อยแล้ว');
      }

      handleCloseModal();
      loadReceipts();
    } catch (error) {
      console.error('Error saving receipt:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกใบเสร็จ');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (values) => {
    try {
      setLoading(true);

      const data = {
        paid_amount: parseFloat(values.paid_amount),
        payment_method: values.payment_method,
        payment_date: values.payment_date,
        notes: values.notes
      }

      await payReceipt(paymentReceipt.id, data);
      message.success('บันทึกการชำระเงินเรียบร้อยแล้ว');
      handleClosePaymentModal();
      loadReceipts();

    } catch (error) {
      console.error('Error recording payment:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกการชำระเงิน');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteReceipt(id);
      message.success('ลบใบเสร็จเรียบร้อยแล้ว');
      loadReceipts();
    } catch (error) {
      console.error('Error deleting receipt:', error);
      message.error('เกิดข้อผิดพลาดในการลบใบเสร็จ');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintTable = async () => {
    try {
      setLoading(true);
      await downloadReceiptsTablePDF(receipts, company, filters, salesStats);
      message.success('สร้างไฟล์ PDF เรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error printing table:', error);
      message.error('ไม่สามารถพิมพ์ตารางได้');
    } finally {
      setLoading(false);
    }
  };

  // Mobile Receipt Card Component
  const ReceiptCard = ({ receipt }) => (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      actions={[
        <Tooltip title="ดูรายละเอียด">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => showDetailDrawer(receipt)}
            size="small"
          />
        </Tooltip>,
        receipt.status !== 'paid' && user.role === 'SUPER_ADMIN' && (
          <Tooltip title="บันทึกการชำระ">
            <Button
              type="text"
              icon={<PayCircleOutlined />}
              onClick={() => showPaymentModal(receipt)}
              size="small"
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
        ),

        user.role === 'SUPER_ADMIN' && (
          <Tooltip title="แก้ไข">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(receipt)}
              size="small"
            />
          </Tooltip>),

        < Tooltip title="พิมพ์" >
          <Button
            type="text"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(receipt)}
            size="small"
            style={{ color: '#7e57c2' }}
          />
        </Tooltip >,
        receipt.status !== 'paid' && user.role === 'SUPER_ADMIN' && (
          <Popconfirm
            title="คุณแน่ใจหรือไม่ที่จะลบใบเสร็จนี้?"
            onConfirm={() => handleDelete(receipt.id)}
            okText="ใช่"
            cancelText="ไม่"
          >
            <Tooltip title="ลบ">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        )
      ].filter(Boolean)}
    >
      <div onClick={() => showDetailDrawer(receipt)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong>{receipt.invoice_number}</strong>
          <Tag color={getReceiptStatusColor(receipt.status)}>
            {getReceiptStatusLabel(receipt.status)}
          </Tag>
        </div>

        {receipt.receipt_number && (
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            เลขที่เสร็จ: {receipt.receipt_number}
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{receipt.contact_name}</div>
          {receipt.company_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>{receipt.company_name}</div>
          )}
        </div>

        <Row gutter={16} style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
          <Col span={12}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {dayjs(receipt.receipt_date).format('DD/MM/YYYY')}
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <DollarOutlined style={{ marginRight: 4 }} />
            ฿{parseFloat(receipt.total_amount).toLocaleString()}
          </Col>
        </Row>

        {receipt.paid_amount > 0 && (
          <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: 4 }}>
            ชำระแล้ว: ฿{parseFloat(receipt.paid_amount).toLocaleString()}
          </div>
        )}

        {receipt.due_date && (
          <div style={{ fontSize: '12px', color: '#666' }}>
            กำหนดชำระ: {dayjs(receipt.due_date).format('DD/MM/YYYY')}
          </div>
        )}
      </div>
    </Card >
  );

  // Table columns
  const columns = [
    {
      title: 'เลขที่เอกสาร',
      key: 'document_number',
      width: 150,
      render: (text, record) => (
        <div>
          <Button
            type="link"
            onClick={() => showDetailDrawer(record)}
            style={{ padding: 0, height: 'auto' }}
          >
            {record.invoice_number}
          </Button>
          {record.receipt_number && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.receipt_number}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'ลูกค้า',
      key: 'customer',
      width: 200,
      render: (text, record) => (
        <div>
          <div>{record.contact_name}</div>
          {record.company_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.company_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'วันที่',
      dataIndex: 'receipt_date',
      key: 'receipt_date',
      width: 120,
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'กำหนดชำระ',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'ยอดเงิน',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <div>
          <div>{parseFloat(record.total_amount).toLocaleString()}</div>
          {record.paid_amount > 0 && (
            <div style={{ fontSize: '12px', color: '#52c41a' }}>
              ชำระแล้ว {parseFloat(record.paid_amount).toLocaleString()}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'ภาษี',
      key: 'tax_amount',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <div>{parseFloat(record.tax_amount).toLocaleString()}</div>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getReceiptStatusColor(status)}>
          {getReceiptStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'การดำเนินการ',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="ดูรายละเอียด">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showDetailDrawer(record)}
            />
          </Tooltip>

          {record.status !== 'paid' && user.role === 'SUPER_ADMIN' && (
            <>
              <Tooltip title="บันทึกการชำระ">
                <Button
                  type="primary"
                  icon={<PayCircleOutlined />}
                  size="small"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => showPaymentModal(record)}
                />
              </Tooltip>

            </>
          )}

          {user.role === 'SUPER_ADMIN' && (
            <>
              <Tooltip title="แก้ไข">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ backgroundColor: '#faad14', borderColor: '#faad14' }}
                  onClick={() => showEditModal(record)}
                />
              </Tooltip>

            </>
          )}


          <Tooltip title="พิมพ์">
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              size="small"
              style={{ backgroundColor: '#7e57c2', borderColor: '#7e57c2' }}
              onClick={() => handlePrint(record)}
            />
          </Tooltip>

          {record.status !== 'paid' && user.role === 'SUPER_ADMIN' && (
            <Popconfirm
              title="คุณแน่ใจหรือไม่ที่จะลบใบเสร็จนี้?"
              onConfirm={() => handleDelete(record.id)}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Tooltip title="ลบ">
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handlePrint = async (record) => {
    try {
      message.loading('กำลังสร้าง PDF...', 1);

      const response = await getReceiptById(record.id);

      if (response.data.success) {
        const receiptData = response.data.data;

        const companyInfo = {
          name: company.company_name || 'ชื่อบริษัทของคุณ',
          address: 'ที่อยู่บริษัทของคุณ',
          phone: 'หมายเลขโทรศัพท์บริษัท'
        };

        setPdfPreviewReceipt(receiptData);
        setPdfModalVisible(true);

      }
    } catch (error) {
      console.error('PDF generation error:', error);
      message.error('เกิดข้อผิดพลาดในการสร้าง PDF: ' + error.message);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleCustomerFilter = (value) => {
    setFilters(prev => ({ ...prev, contact_id: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', contact_id: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>

      {/* Modal แสดง PDF */}
      <Modal
        title="ใบเสร็จ/ใบแจ้งหนี้"
        visible={pdfModalVisible}
        onCancel={handleClosePdfModal}
        footer={[
          <Button key="download" type="primary" onClick={handleDownloadPdf} disabled={!pdfDataUrl || pdfLoading}>
            ดาวน์โหลด PDF
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
        bodyStyle={{ height: isMobile ? '90vh' : '650px' }}
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={printType}
            onChange={(e) => {
              const value = e.target.value;
              setPrintType(value);
              localStorage.setItem('PRINT_TYPE', value);
            }}
          >
            <Radio value="full">แบบฟอร์มเต็ม</Radio>
            <Radio value="dot">แบบฟอร์มสำหรับเครื่อง Dot Matrix</Radio>
          </Radio.Group>
        </div>
        {pdfLoading && (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" />
          </div>
        )}
        {!pdfLoading && pdfDataUrl && (
          <iframe
            key={`${printType}-${pdfDataUrl.length}`}
            src={pdfDataUrl}
            style={{ width: '100%', height: '90%' }}
            frameBorder="0"
            title="ตัวอย่างเอกสาร"
          />
        )}
      </Modal>

      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <FileTextOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
          จัดการใบเสร็จ/ใบแจ้งหนี้ (พร้อมการคำนวณภาษี)
        </Title>
      </div>

      {/* Sales Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"}>
            <Statistic
              title="ยอดขายรวม (เดือนนี้)"
              value={salesStats.total_sales || 0}
              precision={2}
              valueStyle={{
                color: '#3f8600',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<DollarOutlined />}
              suffix="บาท"
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"}>
            <Statistic
              title="ใบเสร็จทั้งหมด"
              value={salesStats.total_receipts || 0}
              valueStyle={{
                color: '#1890ff',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"}>
            <Statistic
              title="ชำระแล้ว"
              value={salesStats.paid_receipts || 0}
              valueStyle={{
                color: '#52c41a',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<MoneyCollectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"}>
            <Statistic
              title="ค้างชำระ"
              value={salesStats.outstanding_amount || 0}
              precision={2}
              valueStyle={{
                color: '#cf1322',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<CalendarOutlined />}
              suffix="บาท"
            />
          </Card>
        </Col>
      </Row>

      {/* Desktop Status Filter Badges */}
      {!isMobile && (
        <Card style={{ marginBottom: '16px' }}>
          <Space size="large" wrap>
            <Badge
              count={statusCounts.find(s => s.status === 'invoice')?.count || 0}
              color="blue"
            >
              <Button
                type={filters.status === 'invoice' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'invoice' ? '' : 'invoice')}
              >
                ใบแจ้งหนี้
              </Button>
            </Badge>

            <Badge
              count={statusCounts.find(s => s.status === 'paid')?.count || 0}
              color="green"
            >
              <Button
                type={filters.status === 'paid' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'paid' ? '' : 'paid')}
              >
                ชำระแล้ว
              </Button>
            </Badge>

            <Badge
              count={statusCounts.find(s => s.status === 'partial')?.count || 0}
              color="orange"
            >
              <Button
                type={filters.status === 'partial' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'partial' ? '' : 'partial')}
              >
                ชำระบางส่วน
              </Button>
            </Badge>

            <Badge
              count={statusCounts.find(s => s.status === 'overdue')?.count || 0}
              color="red"
            >
              <Button
                type={filters.status === 'overdue' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'overdue' ? '' : 'overdue')}
              >
                เกินกำหนด
              </Button>
            </Badge>
          </Space>
        </Card>
      )}

      {/* Desktop Search and Filters */}
      {!isMobile && (
        <Card style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={24} style={{ marginBottom: 16 }}>
              <Space wrap>
                <Text strong>
                  <CalendarOutlined /> กรองตามช่วงเวลา:
                </Text>
                <Button
                  size="small"
                  type={selectedQuickFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => handleQuickDateFilter('all')}
                >
                  ทั้งหมด
                </Button>
                <Button
                  size="small"
                  type={selectedQuickFilter === 'this_month' ? 'primary' : 'default'}
                  onClick={() => handleQuickDateFilter('this_month')}
                >
                  เดือนนี้
                </Button>
                <Button
                  size="small"
                  type={selectedQuickFilter === 'last_month' ? 'primary' : 'default'}
                  onClick={() => handleQuickDateFilter('last_month')}
                >
                  เดือนที่แล้ว
                </Button>
                <Button
                  size="small"
                  type={selectedQuickFilter === 'last_3_months' ? 'primary' : 'default'}
                  onClick={() => handleQuickDateFilter('last_3_months')}
                >
                  รอบ 3 เดือน
                </Button>
                <Button
                  size="small"
                  type={selectedQuickFilter === 'last_6_months' ? 'primary' : 'default'}
                  onClick={() => handleQuickDateFilter('last_6_months')}
                >
                  รอบ 6 เดือน
                </Button>
                <RangePicker
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  placeholder={['วันเริ่มต้น', 'วันสิ้นสุด']}
                  format="DD/MM/YYYY"
                  style={{ width: 240 }}
                />
                {(selectedDateRange[0] || selectedDateRange[1]) && (
                  <Button
                    size="small"
                    type="link"
                    onClick={clearDateFilter}
                    danger
                  >
                    ล้างตัวกรอง
                  </Button>
                )}
              </Space>
            </Col>
            <Col span={3}>
              <Input.Search
                placeholder="ค้นหาเลขที่เอกสาร, ลูกค้า"
                allowClear
                onSearch={handleSearch}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="เลือกสถานะ"
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={handleStatusFilter}
              >
                {statusOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="เลือกลูกค้า"
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                value={filters.contact_id}
                onChange={handleCustomerFilter}
              >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.name}{customer.company_name && ` (${customer.company_name})`}
                  </Option>
                ))}
              </Select>
            </Col>



            <>
              <Col span={4}>
                <Space>
                  <Button onClick={clearFilters}>ล้าง</Button>
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handlePrintTable}
                  >
                    พิมพ์ตาราง
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={showCreateModal}
                  >
                    สร้างใบเสร็จ
                  </Button>
                </Space>
              </Col>
            </>



          </Row>
        </Card>
      )}

      {/* Mobile Search and Status Filters */}
      {isMobile && (
        <>
          <Card style={{ marginBottom: '16px' }}>
            <Input
              placeholder="ค้นหาเลขที่เอกสาร, ลูกค้า"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <Space wrap>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setMobileFiltersVisible(true)}
                  size="small"
                >
                  ตัวกรอง
                </Button>
                <Badge
                  count={statusCounts.find(s => s.status === 'invoice')?.count || 0}
                  color="blue"
                  size="small"
                >
                  <Button
                    type={filters.status === 'invoice' ? 'primary' : 'default'}
                    onClick={() => handleStatusFilter(filters.status === 'invoice' ? '' : 'invoice')}
                    size="small"
                  >
                    ใบแจ้งหนี้
                  </Button>
                </Badge>
                <Badge
                  count={statusCounts.find(s => s.status === 'paid')?.count || 0}
                  color="green"
                  size="small"
                >
                  <Button
                    type={filters.status === 'paid' ? 'primary' : 'default'}
                    onClick={() => handleStatusFilter(filters.status === 'paid' ? '' : 'paid')}
                    size="small"
                  >
                    ชำระแล้ว
                  </Button>
                </Badge>
              </Space>
            </div>
          </Card>

          {/* Mobile Filters Drawer */}
          <Drawer
            title="ตัวกรองข้อมูล"
            placement="bottom"
            height="auto"
            open={mobileFiltersVisible}
            onClose={() => setMobileFiltersVisible(false)}
          >
            <div style={{ padding: '16px 0' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>สถานะ</label>
                <Select
                  placeholder="เลือกสถานะ"
                  value={filters.status}
                  onChange={handleStatusFilter}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>ลูกค้า</label>
                <Select
                  placeholder="เลือกลูกค้า"
                  value={filters.contact_id}
                  onChange={handleCustomerFilter}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name} {customer.company_name && `(${customer.company_name})`}
                    </Option>
                  ))}
                </Select>
              </div>

              <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                <Space>
                  <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                  <Button
                    type="primary"
                    onClick={() => setMobileFiltersVisible(false)}
                  >
                    ตกลง
                  </Button>
                </Space>
              </div>
            </div>
          </Drawer>
        </>
      )}

      {/* Data Display */}
      <Card>
        {isMobile ? (
          <List
            loading={loading}
            dataSource={receipts}
            renderItem={(receipt) => <ReceiptCard key={receipt.id} receipt={receipt} />}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รายการ`,
              size: 'small'
            }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={receipts}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รายการ`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}

            footer={() => {

              const totalAmount = receipts.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0)
              const totalTax = receipts.reduce((sum, item) => sum + parseFloat(item.tax_amount || 0), 0)

              return (
                <div style={{
                  padding: '8px 16px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 18
                }}>
                  <div>
                    <Text strong>ยอดรวมทั้งสิ้น:</Text>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div><Text strong>จำนวนเงิน:</Text> {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    <div><Text strong>ภาษีขาย:</Text> {totalTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
              )
            }}

          />
        )}
      </Card>

      {/* Float Button for Mobile Add */}
      {isMobile && (
        <FloatButton
          icon={<PlusOutlined />}
          type="primary"
          onClick={showCreateModal}
          style={{ right: 24, bottom: 24 }}
        />
      )}

      {/* Create/Edit Receipt Form */}
      {isMobile ? (
        <Drawer
          title={editingReceipt ? 'แก้ไขใบเสร็จ' : 'สร้างใบเสร็จใหม่'}
          open={modalVisible}
          onClose={handleCloseModal}
          width="100%"
          placement="right"
          destroyOnClose
          extra={
            <Space>
              <Button onClick={handleCloseModal}>ยกเลิก</Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={loading}
              >
                บันทึก
              </Button>
            </Space>
          }
        >
          <ReceiptForm
            form={form}
            customers={customers}
            products={products}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            loading={loading}
            isEditing={!!editingReceipt}
            isMobile={isMobile}
            barcodeMode={barcodeMode}
            setBarcodeMode={setBarcodeMode}
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            barcodeLoading={barcodeLoading}
            barcodeInputRef={barcodeInputRef}
            handleBarcodeScanned={handleBarcodeScanned}
            handleBarcodeInputChange={handleBarcodeInputChange}
            handleBarcodeInputKeyPress={handleBarcodeInputKeyPress}
            clearBarcodeInput={clearBarcodeInput}
            vatModeOptions={vatModeOptions || []}
            calculateTotals={calculateTotals}
          />
        </Drawer>
      ) : (
        <Modal
          title={editingReceipt ? 'แก้ไขใบเสร็จ' : 'สร้างใบเสร็จใหม่'}
          open={modalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={900}
          destroyOnClose
        >
          <ReceiptForm
            form={form}
            customers={customers}
            products={products}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            loading={loading}
            isEditing={!!editingReceipt}
            isMobile={isMobile}
            barcodeMode={barcodeMode}
            setBarcodeMode={setBarcodeMode}
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            barcodeLoading={barcodeLoading}
            barcodeInputRef={barcodeInputRef}
            onValuesChange={handleFormChange}
            handleBarcodeScanned={handleBarcodeScanned}
            handleBarcodeInputChange={handleBarcodeInputChange}
            handleBarcodeInputKeyPress={handleBarcodeInputKeyPress}
            clearBarcodeInput={clearBarcodeInput}
            vatModeOptions={vatModeOptions || []}
            calculateTotals={calculateTotals}
          />
        </Modal>
      )}

      {/* Payment Modal */}
      <Modal
        title="บันทึกการชำระเงิน"
        open={paymentModalVisible}
        onCancel={handleClosePaymentModal}
        footer={null}
        width={isMobile ? '100%' : 500}
      >
        <PaymentForm
          form={paymentForm}
          receipt={paymentReceipt}
          paymentMethods={paymentMethods}
          onSubmit={handlePayment}
          onCancel={handleClosePaymentModal}
          loading={loading}
          isMobile={isMobile}
        />
      </Modal>

      {/* Receipt Detail Drawer */}
      <Drawer
        title="รายละเอียดใบเสร็จ"
        placement="right"
        size={isMobile ? "default" : "large"}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        width={isMobile ? "100%" : undefined}
      >
        {selectedReceipt && <ReceiptDetail receipt={selectedReceipt} isMobile={isMobile} vatModeOptions={vatModeOptions || []} />}
      </Drawer>
    </div>
  );
};

// Receipt Form Component with VAT Calculation
const ReceiptForm = ({
  form,
  customers,
  products,
  onSubmit,
  onCancel,
  loading,
  isEditing,
  isMobile,
  barcodeMode,
  setBarcodeMode,
  barcodeInput,
  setBarcodeInput,
  barcodeLoading,
  barcodeInputRef,
  handleBarcodeScanned,
  handleBarcodeInputChange,
  handleBarcodeInputKeyPress,
  clearBarcodeInput,
  vatModeOptions,
  calculateTotals
}) => {


  // Status options
  const statusOptions = [
    { value: 'invoice', label: 'ใบแจ้งหนี้', color: 'processing' },
    { value: 'paid', label: 'ชำระแล้ว', color: 'success' },
    { value: 'partial', label: 'ชำระบางส่วน', color: 'warning' },
    { value: 'overdue', label: 'เกินกำหนด', color: 'error' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'default' },
  ];


  const [calcValues, setCalcValues] = useState({
    subtotal: 0,
    discount: 0,
    subtotalAfterDiscount: 0,
    taxAmount: 0,
    totalAmount: 0
  });

  // Update calculations when form values change
  const updateCalculations = () => {
    const items = form.getFieldValue('items') || [];
    const discount = form.getFieldValue('discount') || 0;
    const taxRate = form.getFieldValue('tax_rate') || 0;
    const vatMode = form.getFieldValue('vat_mode') || 'exclude';

    const calculations = calculateTotals(items, discount, taxRate, vatMode);
    setCalcValues(calculations);
  };


  const handleFormChange = () => {
    const items = form.getFieldValue('items') || [];
    const discount = form.getFieldValue('discount') || 0;
    const taxRate = form.getFieldValue('tax_rate') || 7;
    const currentVatMode = form.getFieldValue('vat_mode') || 'exclude';

    const calculations = calculateTotals(items, discount, taxRate, currentVatMode);
    setCalcValues(calculations);

  };


  // โหลดข้อมูลครั้งแรก
  useEffect(() => {
    updateCalculations();
    //console.log('calcValues', calcValues)
  }, [calcValues]);




  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      onValuesChange={handleFormChange} // ⬅️ ตรงนี้
      initialValues={{
        receipt_date: dayjs(),
        tax_rate: 7,
        vat_mode: 'include',
        discount: 0,
        items: [{ description: '-', quantity: 1, unit_price: 0 }]
      }}
    >
      <Row gutter={isMobile ? [0, 16] : 16}>

        {isEditing && (
          <>
            <Col xs={24} sm={12}>
              <Form.Item
                name="invoice_number"
                label="เลขที่ใบแจ้งหนี้"
                rules={[{ required: true, message: 'หมายเลขเอกสาร' }]}
              >
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="receipt_number"
                label="เลขที่ใบเสร็จ"
                rules={[{ required: true, message: 'หมายเลขเอกสาร' }]}
              >
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </>
        )}

        <Col xs={24} sm={12}>
          <Form.Item
            name="contact_id"
            label="ลูกค้า"
            rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
          >
            <Select
              placeholder="เลือกลูกค้า"
              showSearch
              optionFilterProp="children"
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.name} {customer.company_name && `(${customer.company_name})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="receipt_date"
            label="วันที่"
            rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? [0, 16] : 16}>
        <Col xs={24} sm={isEditing ? 12 : 24}>
          <Form.Item
            name="due_date"
            label="กำหนดชำระ"
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
        {isEditing && (
          <Col xs={24} sm={12}>
            <Form.Item
              name="status"
              label="สถานะ"
            >
              <Select placeholder="เลือกสถานะ">
                {statusOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      <Divider>รายการสินค้า/บริการ</Divider>

      {/* Barcode Scanner Section */}
      <Card style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BarcodeOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            <span style={{ fontWeight: 'bold', color: '#1890ff' }}>สแกนบาร์โค้ด</span>
          </div>
          <Switch
            checked={barcodeMode}
            onChange={setBarcodeMode}
            checkedChildren={<ScanOutlined />}
            unCheckedChildren="OFF"
          />
        </div>

        {barcodeMode && (
          <>
            <Alert
              message="สแกนบาร์โค้ดหรือพิมพ์รหัสบาร์โค้ดแล้วกด Enter"
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                ref={barcodeInputRef}
                placeholder="สแกนหรือพิมพ์บาร์โค้ด..."
                value={barcodeInput}
                onChange={handleBarcodeInputChange}
                onPressEnter={handleBarcodeInputKeyPress}
                prefix={<BarcodeOutlined />}
                suffix={
                  barcodeLoading ? (
                    <div className="loading-spinner" />
                  ) : barcodeInput ? (
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={clearBarcodeInput}
                    />
                  ) : undefined
                }
                style={{ flex: 1 }}
                autoFocus={barcodeMode}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => handleBarcodeScanned(barcodeInput)}
                disabled={!barcodeInput.trim() || barcodeLoading}
                loading={barcodeLoading}
              >
                ค้นหา
              </Button>
            </div>
          </>
        )}
      </Card>

      <Form.List name="items">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Card key={key} style={{ marginBottom: 16 }} size="small">
                <Row gutter={isMobile ? [0, 12] : 16}>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'product_id']}
                      label="สินค้า"
                    >
                      <Select
                        placeholder="เลือกสินค้า"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        onChange={(value) => {
                          if (value) {
                            const product = products.find(p => p.id === value);
                            if (product) {
                              const items = form.getFieldValue('items');
                              items[name] = {
                                ...items[name],
                                description: product.name,
                                unit_price: product.price
                              };
                              form.setFieldsValue({ items });
                            }
                          }
                        }}
                      >
                        {products.map(product => (
                          <Option key={product.id} value={product.id}>
                            {product.name} - ฿{product.price}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={18}>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label="รายการ"
                      rules={[{ required: true, message: 'กรุณากรอกรายการ' }]}
                    >
                      <Input placeholder="รายละเอียดสินค้า/บริการ" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={isMobile ? [0, 12] : 16}>
                  <Col xs={12} sm={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      label="จำนวน"
                      rules={[{ required: true, message: 'กรุณากรอกจำนวน' }]}
                    >
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'unit_price']}
                      label="ราคาต่อหน่วย"
                      rules={[{ required: true, message: 'กรุณากรอกราคา' }]}
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="รวม" shouldUpdate={(prevValues, currentValues) => {
                      const prevItems = prevValues.items || [];
                      const currentItems = currentValues.items || [];
                      return JSON.stringify(prevItems) !== JSON.stringify(currentItems);
                    }}>
                      {({ getFieldValue }) => {
                        const items = getFieldValue('items') || [];
                        const item = items[name] || {};
                        const total = (item.quantity || 0) * (item.unit_price || 0);
                        return (
                          <InputNumber
                            value={total}
                            disabled
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          />
                        );
                      }}
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={2}>
                    <Form.Item label={isMobile ? "ลบ" : " "}>
                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        disabled={fields.length === 1}
                        style={{ width: '100%' }}
                      >
                        ลบ
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
            >
              เพิ่มรายการ
            </Button>
          </>
        )}
      </Form.List>

      <Divider>ข้อมูลเพิ่มเติม</Divider>

      <Row gutter={isMobile ? [0, 16] : 16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="discount"
            label="ส่วนลด"
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              onChange={handleFormChange}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              addonAfter="บาท"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="tax_rate"
            label="อัตราภาษี (%)"
          >
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              style={{ width: '100%' }}
              onChange={handleFormChange}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* VAT Mode Selection */}
      <Card style={{ marginBottom: 16 }}>
        <Divider>การคำนวณภาษี</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Form.Item
              name="vat_mode"
              label="โหมดการคำนวณภาษี"
              rules={[{ required: true, message: 'กรุณาเลือกโหมดการคำนวณภาษี' }]}
            >
              <Radio.Group
                size={isMobile ? "small" : "default"}
                onChange={handleFormChange}
              >
                <Radio.Button value="no_tax">ไม่มีภาษี</Radio.Button>
                <Radio.Button value="exclude">แยกภาษี (VAT EXCLUDE)</Radio.Button>
                <Radio.Button value="include">รวมภาษี (VAT INCLUDE)</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Alert
              message={
                form.getFieldValue('vat_mode') === 'include'
                  ? "โหมด VAT INCLUDE: ราคาสินค้าที่ป้อนรวมภาษีแล้ว ระบบจะแยกคำนวณภาษีออกจากยอดรวม"
                  : form.getFieldValue('vat_mode') === 'exclude'
                    ? "โหมด VAT EXCLUDE: ราคาสินค้าที่ป้อนยังไม่รวมภาษี ระบบจะคำนวณภาษีเพิ่มเติม"
                    : "โหมดไม่มีภาษี: ระบบจะไม่คำนวณภาษีให้"
              }
              type={
                form.getFieldValue('vat_mode') === 'include'
                  ? 'info'
                  : form.getFieldValue('vat_mode') === 'exclude'
                    ? 'warning'
                    : 'info' // ✅ เปลี่ยนจาก default → info
              }
              showIcon
            />
          </Col>
        </Row>
      </Card>

      {/* Calculation Summary */}
      {calcValues.subtotal && (
        <>
          <Card title="สรุปการคำนวณ" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}><strong>ยอดรวมก่อนภาษี:</strong></Col>
              <Col span={12} style={{ textAlign: 'right' }}>{calcValues.subtotal?.toLocaleString()}</Col>

              <Col span={12}><strong>ส่วนลด:</strong></Col>
              <Col span={12} style={{ textAlign: 'right' }}>{calcValues.discount?.toLocaleString()}</Col>

              <Col span={12}><strong>ยอดหลังส่วนลด:</strong></Col>
              <Col span={12} style={{ textAlign: 'right' }}>{calcValues.subtotalAfterDiscount?.toLocaleString()}</Col>

              <Col span={12}><strong>ภาษีมูลค่าเพิ่ม:</strong></Col>
              <Col span={12} style={{ textAlign: 'right' }}>{calcValues.taxAmount?.toLocaleString()}</Col>

              <Divider style={{ margin: '8px 0' }} />

              <Col span={12}><strong>ยอดรวมทั้งสิ้น:</strong></Col>
              <Col span={12} style={{ textAlign: 'right', fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                {calcValues.totalAmount?.toLocaleString()}
              </Col>
            </Row>
          </Card>
        </>
      )}

      <Form.Item
        name="notes"
        label="หมายเหตุ"
      >
        <TextArea rows={3} placeholder="หมายเหตุเพิ่มเติม" />
      </Form.Item>

      {!isMobile && (
        <Form.Item>
          <Space>
            <Button onClick={onCancel}>
              ยกเลิก
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              บันทึก
            </Button>
          </Space>
        </Form.Item>
      )}
    </Form>
  );
};

// Payment Form Component
const PaymentForm = ({ form, receipt, paymentMethods, onSubmit, onCancel, loading, isMobile }) => {
  if (!receipt) return null;

  const remainingAmount = receipt.total_amount - (receipt.paid_amount || 0);

  return (
    <div>
      <Descriptions column={1} bordered style={{ marginBottom: 16 }} size={isMobile ? "small" : "default"}>
        <Descriptions.Item label="เลขที่เอกสาร">
          {receipt.invoice_number}
        </Descriptions.Item>
        <Descriptions.Item label="ลูกค้า">
          {receipt.contact_name}
        </Descriptions.Item>
        <Descriptions.Item label="ยอดรวม">
          ฿{parseFloat(receipt.total_amount).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="ชำระแล้ว">
          ฿{parseFloat(receipt.paid_amount || 0).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="คงเหลือ">
          <strong style={{ color: '#cf1322' }}>
            ฿{remainingAmount.toLocaleString()}
          </strong>
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{ payment_date: dayjs(receipt.receipt_date) }}
      >
        <Form.Item
          name="paid_amount"
          label="จำนวนเงินที่ชำระ"
          rules={[
            { required: true, message: 'กรุณากรอกจำนวนเงิน' },
            {
              validator: (_, value) => {
                if (value > remainingAmount) {
                  return Promise.reject('จำนวนเงินที่ชำระต้องไม่เกินยอดคงเหลือ');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <InputNumber
            min={0}
            max={remainingAmount}
            step={0.01}
            style={{ width: '100%' }}
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            addonAfter="บาท"
          />
        </Form.Item>

        <Form.Item
          name="payment_method"
          label="วิธีการชำระเงิน"
          rules={[{ required: true, message: 'กรุณาเลือกวิธีการชำระเงิน' }]}
        >
          <Select placeholder="เลือกวิธีการชำระเงิน">
            {paymentMethods.map(method => (
              <Option key={method} value={method}>
                {method}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="payment_date"
          label="วันที่ชำระเงิน"
          rules={[{ required: true, message: 'กรุณาเลือกวันที่ชำระเงิน' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD-MM-YYYY" // แสดงและส่งฟอร์แมต DD-MM-YYYY
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label="หมายเหตุ"
        >
          <TextArea rows={3} placeholder="หมายเหตุการชำระเงิน" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>
              ยกเลิก
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              บันทึกการชำระ
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

const ReceiptDetail = ({ receipt, isMobile, vatModeOptions }) => {
  if (!receipt) return null;

  // แปลง string เป็น number สำหรับการแสดงผลและคำนวณ
  const itemsWithNumber = receipt.items?.map(item => ({
    ...item,
    quantity: parseFloat(item.quantity),
    unit_price: parseFloat(item.unit_price),
    total_price: parseFloat(item.total_price)
  })) || [];

  const receiptToShow = {
    ...receipt,
    subtotal: parseFloat(receipt.subtotal || 0),
    discount: parseFloat(receipt.discount || 0),
    tax_amount: parseFloat(receipt.tax_amount || 0),
    total_amount: parseFloat(receipt.total_amount || 0),
    paid_amount: parseFloat(receipt.paid_amount || 0),
    items: itemsWithNumber
  };

  const statusOptions = [
    { value: 'invoice', label: 'ใบแจ้งหนี้', color: 'processing' },
    { value: 'paid', label: 'ชำระแล้ว', color: 'success' },
    { value: 'partial', label: 'ชำระบางส่วน', color: 'warning' },
    { value: 'overdue', label: 'เกินกำหนด', color: 'error' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'default' },
  ];

  const getStatusColor = status => statusOptions.find(opt => opt.value === status)?.color || 'default';
  const getStatusLabel = status => statusOptions.find(opt => opt.value === status)?.label || status;
  const getVatModeLabel = vatMode => {
    if (!vatMode) return 'ไม่ระบุ';
    if (!vatModeOptions || !Array.isArray(vatModeOptions)) return vatMode;
    return vatModeOptions.find(opt => opt.value === vatMode)?.label || vatMode;
  };

  return (
    <Tabs defaultActiveKey="1">
      {/* ข้อมูลใบเสร็จ */}
      <TabPane tab="ข้อมูลใบเสร็จ" key="1">
        <Descriptions column={isMobile ? 1 : 2} bordered size={isMobile ? "small" : "default"}>
          <Descriptions.Item label="เลขที่ใบแจ้งหนี้" span={isMobile ? 1 : 2}>
            {receiptToShow.invoice_number}
          </Descriptions.Item>
          {receiptToShow.receipt_number && (
            <Descriptions.Item label="เลขที่ใบเสร็จ" span={isMobile ? 1 : 2}>
              {receiptToShow.receipt_number}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="ลูกค้า" span={isMobile ? 1 : 2}>
            <div>
              <div>{receiptToShow.contact_name}</div>
              {receiptToShow.company_name && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {receiptToShow.company_name}
                </div>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="วันที่">
            {dayjs(receiptToShow.receipt_date).format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="กำหนดชำระ">
            {receiptToShow.due_date ? dayjs(receiptToShow.due_date).format('DD/MM/YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="สถานะ" span={isMobile ? 1 : 2}>
            <Tag color={getStatusColor(receiptToShow.status)}>
              {getStatusLabel(receiptToShow.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="โหมดการคำนวณภาษี" span={isMobile ? 1 : 2}>
            {receiptToShow.vat_mode && (
              <Tag color={receiptToShow.vat_mode === 'include' ? 'green' :
                receiptToShow.vat_mode === 'exclude' ? 'blue' : 'default'}>
                {getVatModeLabel(receiptToShow.vat_mode)}
              </Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="ยอดรวมก่อนภาษี">
            ฿{receiptToShow.subtotal.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="ส่วนลด">
            ฿{receiptToShow.discount.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={`ภาษี (${receiptToShow.tax_rate || 0}%)`}>
            ฿{receiptToShow.tax_amount.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="ยอดรวมทั้งสิ้น">
            <strong>฿{receiptToShow.total_amount.toLocaleString()}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="ชำระแล้ว">
            <strong style={{ color: '#52c41a' }}>฿{receiptToShow.paid_amount.toLocaleString()}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="คงเหลือ">
            <strong style={{ color: '#cf1322' }}>
              ฿{(receiptToShow.total_amount - receiptToShow.paid_amount).toLocaleString()}
            </strong>
          </Descriptions.Item>
          {receiptToShow.payment_method && (
            <Descriptions.Item label="วิธีการชำระ" span={isMobile ? 1 : 2}>
              {receiptToShow.payment_method}
            </Descriptions.Item>
          )}
          {receiptToShow.notes && (
            <Descriptions.Item label="หมายเหตุ" span={isMobile ? 1 : 2}>
              {receiptToShow.notes}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="ผู้สร้าง">{receiptToShow.created_by_name}</Descriptions.Item>
          <Descriptions.Item label="วันที่สร้าง">
            {dayjs(receiptToShow.created_at).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        </Descriptions>
      </TabPane>

      {/* รายการสินค้า */}
      <TabPane tab="รายการสินค้า" key="2">
        {receiptToShow.items && receiptToShow.items.length > 0 ? (
          isMobile ? (
            <List
              dataSource={receiptToShow.items}
              renderItem={(item, index) => (
                <Card size="small" style={{ marginBottom: 8 }} title={`รายการที่ ${index + 1}`}>
                  <p><strong>รายการ:</strong> {item.description}</p>
                  <Row gutter={16}>
                    <Col span={8}><p><strong>จำนวน:</strong> {item.quantity}</p></Col>
                    <Col span={8}><p><strong>ราคา/หน่วย:</strong> ฿{item.unit_price.toLocaleString()}</p></Col>
                    <Col span={8}><p><strong>รวม:</strong> ฿{item.total_price.toLocaleString()}</p></Col>
                  </Row>
                </Card>
              )}
            />
          ) : (
            <Table
              dataSource={receiptToShow.items}
              rowKey="id"
              pagination={false}
              columns={[
                { title: 'รายการ', dataIndex: 'description', key: 'description' },
                { title: 'จำนวน', dataIndex: 'quantity', key: 'quantity', align: 'center', width: 100 },
                {
                  title: 'ราคาต่อหน่วย', dataIndex: 'unit_price', key: 'unit_price', align: 'right', width: 120,
                  render: price => `฿${price.toLocaleString()}`
                },
                {
                  title: 'รวม', dataIndex: 'total_price', key: 'total_price', align: 'right', width: 120,
                  render: total => `฿${total.toLocaleString()}`
                }
              ]}
            />
          )
        ) : (
          <Empty description="ไม่มีรายการสินค้า" />
        )}
      </TabPane>
    </Tabs>
  );
};

export default ReceiptsPage;
